import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Plus,
  Search,
  Trash2,
  WorkflowIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNodeStore } from '@/stores/node-store';
import { useOrgStore } from '@/stores/org-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import type { InputValue, NodeParam, NodeStruct } from '@/types/node-types';
import {
  AGENT_NODE_ID,
  DELEGABLE_WORKFLOWS_FIELD_ID,
  parseDelegableWorkflowsValue,
  type DelegableWorkflowEntry,
} from '@/pages/workflow/component/custom-node/generic-node/agent-capability-chips';

/**
 * "Launches" config section for AgentNodes (scoped-mode UI).
 *
 * Surfaces the `delegable_workflows` param — until now only writable by
 * harness scripts, because the param is `advanced` and the sidebar's
 * Advanced Settings drawer is disabled. Each entry becomes a named
 * `run_<alias>` tool at runtime, so alias + when_to_use are the
 * agent-facing tool contract.
 *
 * Conventions:
 * - Workflow names resolve from the org workflow list in the workflow
 *   store — the same source the on-canvas capability chips use — so an
 *   unpublished-but-launchable target (they exist in production) is
 *   never falsely flagged as missing.
 * - Commits the entry ARRAY (not a JSON string) through the standard
 *   param-change path, matching the representation harness scripts
 *   persist, so an edit → revert round-trip is byte-identical in the DB.
 * - Malformed stored JSON renders a recoverable error state (raw value +
 *   reset) instead of crashing, mirroring the chips' malformed path.
 * - Version pin is display-only: pinned entries keep their pin across
 *   edits; picking a specific version is out of scope for this slice.
 */

/**
 * Limits mirroring the backend contract in
 * nodes/agent/delegable_workflows.py (the entry shape and parser live
 * with the capability chips, imported above).
 */
export const MAX_DELEGABLE_WORKFLOWS = 16;
export const ALIAS_MAX_LENGTH = 64;
export const WHEN_TO_USE_MAX_LENGTH = 500;
export const ALIAS_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

const INHERIT_BUDGET = '__node_default__';

const BUDGET_LABELS: Record<string, string> = {
  at_most_one: 'At most one per run',
  exactly_one: 'Exactly one per run',
  unbounded: 'Unbounded',
};

/**
 * Default alias for a newly added workflow: lower_snake_case slug of the
 * workflow name, prefixed when it would not start with a letter, and
 * suffixed `_2`, `_3`, … until unique among `taken`.
 */
export function deriveAliasFromName(name: string, taken: Set<string>): string {
  let base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!/^[a-z]/.test(base)) base = base ? `wf_${base}` : 'workflow';
  base = base.slice(0, ALIAS_MAX_LENGTH).replace(/_+$/g, '');
  if (!taken.has(base)) return base;
  for (let suffix = 2; ; suffix++) {
    const tail = `_${suffix}`;
    const candidate = base.slice(0, ALIAS_MAX_LENGTH - tail.length) + tail;
    if (!taken.has(candidate)) return candidate;
  }
}

/**
 * Insert the `delegable_workflows` param into an agent node's struct.
 *
 * Agent nodes persisted before the param existed have no row for it, and
 * the store's `handleParameterChange` silently no-ops on unknown fields.
 * Clones the canonical param definition from the fetched node catalog
 * when available, falling back to the minimal shape the backend
 * re-instantiation path (`field_id` lookup on the node's declared
 * params) requires.
 */
function insertDelegableParam(reactFlowNodeId: string) {
  const nodeStore = useNodeStore.getState();
  const catalogAgent = Object.values(nodeStore.groupedNodes ?? {})
    .flat()
    .find((candidate) => candidate.node_id === AGENT_NODE_ID);
  const catalogParam = catalogAgent?.params?.find(
    (candidate) => candidate.field_id === DELEGABLE_WORKFLOWS_FIELD_ID,
  );
  const newParam: NodeParam = catalogParam
    ? { ...structuredClone(catalogParam), value: [] }
    : ({
        field_id: DELEGABLE_WORKFLOWS_FIELD_ID,
        display_name: 'Delegable Workflows',
        advanced: true,
        required: false,
        show: false,
        value: [],
      } as unknown as NodeParam);

  nodeStore.setNodes(
    nodeStore.nodes.map((node) =>
      node.id !== reactFlowNodeId ||
      node.data.struct.params.some(
        (candidate) => candidate.field_id === DELEGABLE_WORKFLOWS_FIELD_ID,
      )
        ? node
        : {
            ...node,
            data: {
              ...node.data,
              struct: {
                ...node.data.struct,
                params: [...node.data.struct.params, newParam],
              },
            },
          },
    ),
  );
}

interface NodeLaunchesSectionProps {
  struct: NodeStruct;
  /** The workflow being edited — excluded from the picker to prevent trivial self-launch loops. */
  currentWorkflowId?: string;
  isDisabled?: boolean;
  onValueChange: (value: InputValue) => void;
}

export function NodeLaunchesSection({
  struct,
  currentWorkflowId,
  isDisabled = false,
  onValueChange,
}: NodeLaunchesSectionProps) {
  const orgId = useOrgStore((state) => state.currentOrg?.orgid);
  const savedWorkflows = useWorkflowStore((state) => state.savedWorkflows);
  const isLoadingWorkflows = useWorkflowStore(
    (state) => state.isLoadingWorkflows,
  );

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [confirmRemoveIndex, setConfirmRemoveIndex] = useState<number | null>(
    null,
  );

  const param = struct.params?.find(
    (field) => field.field_id === DELEGABLE_WORKFLOWS_FIELD_ID,
  );

  const rawValue = param?.value;
  const entries = useMemo(
    () => parseDelegableWorkflowsValue(rawValue),
    [rawValue],
  );
  const parseFailed = entries === null;
  const safeEntries = useMemo(() => entries ?? [], [entries]);

  const isAgentNode = struct.node_id === AGENT_NODE_ID;

  // Lazily load the org workflow list (shared store cache, same as the
  // canvas chips) so names resolve on a direct editor-URL load.
  useEffect(() => {
    if (!orgId || !isAgentNode) return;
    const { savedWorkflows: loaded, isLoadingWorkflows: loading } =
      useWorkflowStore.getState();
    if (loaded.length > 0 || loading) return;
    useWorkflowStore
      .getState()
      .getWorkflowList(orgId)
      .catch(() => {
        // Name resolution is best-effort; entries fall back to raw ids.
      });
  }, [orgId, isAgentNode]);

  const workflowNamesById = useMemo(() => {
    const map = new Map<string, string>();
    for (const workflow of savedWorkflows) {
      map.set(workflow.workflow_id, workflow.name);
    }
    return map;
  }, [savedWorkflows]);

  const pickerOptions = useMemo(() => {
    const query = pickerSearch.trim().toLowerCase();
    return savedWorkflows
      .filter((workflow) => workflow.workflow_id !== currentWorkflowId)
      .filter(
        (workflow) =>
          !query || (workflow.name ?? '').toLowerCase().includes(query),
      );
  }, [savedWorkflows, currentWorkflowId, pickerSearch]);

  if (!isAgentNode) return null;

  const commit = (next: DelegableWorkflowEntry[]) => {
    // Agent nodes persisted before the param existed carry no row for it;
    // materialize one first so the standard param-change path can bind.
    if (param === undefined) {
      insertDelegableParam(struct.react_flow_generated_id);
    }
    // Persist the array itself (not a JSON string): it is what the
    // backend validators normalize to and what existing configs store.
    onValueChange(next as unknown as InputValue);
  };

  const updateEntry = (
    index: number,
    patch: Partial<DelegableWorkflowEntry>,
  ) => {
    commit(
      safeEntries.map((entry, position) =>
        position === index ? { ...entry, ...patch } : entry,
      ),
    );
  };

  const addWorkflow = (workflowId: string, workflowName: string) => {
    const taken = new Set(safeEntries.map((entry) => entry.alias));
    commit([
      ...safeEntries,
      {
        workflow_id: workflowId,
        version_id: null,
        alias: deriveAliasFromName(workflowName, taken),
        when_to_use: '',
        launch_budget: null,
      },
    ]);
    setPickerOpen(false);
    setPickerSearch('');
  };

  const removeEntry = (index: number) => {
    setConfirmRemoveIndex(null);
    commit(safeEntries.filter((_, position) => position !== index));
  };

  if (parseFailed) {
    const raw =
      typeof rawValue === 'string'
        ? rawValue
        : JSON.stringify(rawValue, null, 2);
    return (
      <div className='border-t p-4 space-y-2' data-testid='launches-section'>
        <span className='text-sm font-medium'>Launches</span>
        <p className='flex items-center gap-1 text-xs text-destructive'>
          <AlertTriangle className='h-3.5 w-3.5 shrink-0' />
          The stored launch configuration is malformed JSON and cannot be edited
          here.
        </p>
        <pre
          className='max-h-40 overflow-auto rounded-md bg-muted p-2 text-xs whitespace-pre-wrap break-all'
          data-testid='launches-malformed-raw'
        >
          {raw}
        </pre>
        <Button
          type='button'
          size='sm'
          variant='outline'
          disabled={isDisabled}
          onClick={() => commit([])}
          data-testid='launches-reset-malformed'
        >
          Reset launch configuration
        </Button>
      </div>
    );
  }

  return (
    <div className='border-t p-4 space-y-3' data-testid='launches-section'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium'>Launches</span>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              disabled={
                isDisabled || safeEntries.length >= MAX_DELEGABLE_WORKFLOWS
              }
              data-testid='launches-add-button'
            >
              Add workflow
              <Plus className='size-4' />
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-72 p-2' align='end'>
            <div className='relative mb-2'>
              <Search className='absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
              <Input
                value={pickerSearch}
                onChange={(event) => setPickerSearch(event.target.value)}
                placeholder='Search workflows…'
                className='h-8 pl-7 text-sm'
                autoFocus
                data-testid='launches-picker-search'
              />
            </div>
            <div className='max-h-56 overflow-y-auto'>
              {isLoadingWorkflows && (
                <p className='px-2 py-1.5 text-xs text-muted-foreground'>
                  Loading workflows…
                </p>
              )}
              {!isLoadingWorkflows && pickerOptions.length === 0 && (
                <p className='px-2 py-1.5 text-xs text-muted-foreground'>
                  No matching workflows.
                </p>
              )}
              {pickerOptions.map((workflow) => (
                <button
                  key={workflow.workflow_id}
                  type='button'
                  className='flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted'
                  onClick={() =>
                    addWorkflow(workflow.workflow_id, workflow.name ?? '')
                  }
                  data-testid={`launches-picker-option-${workflow.workflow_id}`}
                >
                  <WorkflowIcon className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />
                  <span className='truncate'>
                    {workflow.name || workflow.workflow_id}
                  </span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {safeEntries.length === 0 && (
        <p className='text-xs text-muted-foreground'>
          No launch targets configured. Add a workflow to give this agent a
          scoped <code>run_&lt;alias&gt;</code> tool for it.
        </p>
      )}

      {safeEntries.map((entry, index) => {
        const resolvedName = workflowNamesById.get(entry.workflow_id);
        const listLoaded = !isLoadingWorkflows && savedWorkflows.length > 0;
        const isMissing = !!entry.workflow_id && listLoaded && !resolvedName;
        const aliasInvalid = !!entry.alias && !ALIAS_PATTERN.test(entry.alias);
        const aliasMissing = !entry.alias;
        const duplicateAlias =
          !!entry.alias &&
          safeEntries.some(
            (other, position) =>
              position !== index && other.alias === entry.alias,
          );

        return (
          <div
            key={`${entry.workflow_id}-${index}`}
            className='space-y-2 rounded-md border p-3'
            data-testid={`launch-entry-${index}`}
          >
            <div className='flex items-center justify-between gap-2'>
              <span
                className='min-w-0 truncate text-sm font-medium'
                title={entry.workflow_id}
              >
                {resolvedName || entry.workflow_id || 'Unknown workflow'}
              </span>
              {confirmRemoveIndex === index ? (
                <span className='flex shrink-0 items-center gap-1'>
                  <Button
                    type='button'
                    size='sm'
                    variant='destructive'
                    onClick={() => removeEntry(index)}
                    data-testid={`launch-remove-confirm-${index}`}
                  >
                    Remove
                  </Button>
                  <Button
                    type='button'
                    size='sm'
                    variant='ghost'
                    onClick={() => setConfirmRemoveIndex(null)}
                    data-testid={`launch-remove-cancel-${index}`}
                  >
                    Cancel
                  </Button>
                </span>
              ) : (
                <Button
                  type='button'
                  size='icon'
                  variant='ghost'
                  aria-label={`Remove launch target ${index + 1}`}
                  disabled={isDisabled}
                  onClick={() => setConfirmRemoveIndex(index)}
                  data-testid={`launch-remove-${index}`}
                >
                  <Trash2 className='h-3.5 w-3.5' />
                </Button>
              )}
            </div>

            {isMissing && (
              <p className='flex items-center gap-1 text-xs text-destructive'>
                <AlertTriangle className='h-3.5 w-3.5 shrink-0' />
                This workflow was not found in the org. Remove the entry or
                restore the workflow; launches will fail until then.
              </p>
            )}

            <div className='space-y-1'>
              <Label
                htmlFor={`launch-alias-${struct.react_flow_generated_id}-${index}`}
              >
                Alias (tool name: run_&lt;alias&gt;)
              </Label>
              <Input
                id={`launch-alias-${struct.react_flow_generated_id}-${index}`}
                value={entry.alias}
                onChange={(event) =>
                  updateEntry(index, { alias: event.target.value })
                }
                autoComplete='off'
                spellCheck={false}
                disabled={isDisabled}
              />
              {(aliasMissing || aliasInvalid) && (
                <p className='flex items-center gap-1 text-xs text-destructive'>
                  <AlertTriangle className='h-3.5 w-3.5 shrink-0' />
                  {aliasMissing
                    ? 'Alias is required.'
                    : `Alias must be lower_snake_case (letters, digits, underscores; starts with a letter; at most ${ALIAS_MAX_LENGTH} chars).`}
                </p>
              )}
              {duplicateAlias && (
                <p
                  className='flex items-center gap-1 text-xs text-destructive'
                  data-testid={`launch-alias-duplicate-${index}`}
                >
                  <AlertTriangle className='h-3.5 w-3.5 shrink-0' />
                  This alias is already used by another launch target.
                </p>
              )}
            </div>

            <div className='space-y-1'>
              <Label
                htmlFor={`launch-when-${struct.react_flow_generated_id}-${index}`}
              >
                When to use ({entry.when_to_use.length}/{WHEN_TO_USE_MAX_LENGTH}
                )
              </Label>
              <Textarea
                id={`launch-when-${struct.react_flow_generated_id}-${index}`}
                value={entry.when_to_use}
                onChange={(event) =>
                  updateEntry(index, { when_to_use: event.target.value })
                }
                placeholder='Launch this workflow when… (shown to the agent as the tool description)'
                rows={3}
                disabled={isDisabled}
              />
              {!entry.when_to_use.trim() && (
                <p className='text-xs text-muted-foreground'>
                  Required — this text is the tool description the agent sees.
                </p>
              )}
            </div>

            <div className='space-y-1'>
              <Label>Launch budget</Label>
              <Select
                value={entry.launch_budget || INHERIT_BUDGET}
                onValueChange={(budget) =>
                  updateEntry(index, {
                    launch_budget: budget === INHERIT_BUDGET ? null : budget,
                  })
                }
                disabled={isDisabled}
              >
                <SelectTrigger
                  aria-label={`Launch budget for entry ${index + 1}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={INHERIT_BUDGET}>
                    Node default (at most one per run)
                  </SelectItem>
                  {Object.entries(BUDGET_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <p
              className='text-xs text-muted-foreground'
              title={entry.version_id || undefined}
            >
              Version:{' '}
              {entry.version_id
                ? `pinned (${entry.version_id.slice(0, 8)}…)`
                : 'latest published'}
            </p>
          </div>
        );
      })}

      {safeEntries.length >= MAX_DELEGABLE_WORKFLOWS && (
        <p className='text-xs text-muted-foreground'>
          Maximum of {MAX_DELEGABLE_WORKFLOWS} launch targets reached.
        </p>
      )}
    </div>
  );
}
